import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import Swiper from 'swiper';
import { A11y, Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { AnalyticsService } from '../core/analytics.service';
import { ApiClientService } from '../core/api-client.service';
import { CommerceCartService } from '../core/commerce-cart.service';
import { I18nService } from '../core/i18n.service';
import { PageDataService } from '../core/page-data.service';
import { SiteFooterComponent } from '../shell/site-footer.component';
import { SiteHeaderComponent } from '../shell/site-header.component';

export interface ThemeRenderPayload {
  html: string;
  stylesheet: string;
  themeId: string;
  themeVersion: string;
  publicationID?: number;
  preview: boolean;
  tokens: Record<string, string>;
  metrics?: Record<string, number>;
  experience?: ThemeExperience;
}

interface ThemeCarouselExperience {selector:string;maxWidth?:number;slidesPerView?:number|'auto';spaceBetween?:number;autoplayMs?:number;loop?:boolean;breakpoints?:Record<string,{slidesPerView:number;spaceBetween?:number}>}
interface ThemeExperience {auth?:{mode:'modal'|'drawer'|'page';variant?:string};carousels?:ThemeCarouselExperience[]}

@Component({
  selector: 'app-theme-storefront',
  imports: [CommonModule, SiteFooterComponent, SiteHeaderComponent],
  template: `
    @if (render(); as theme) {
      <div class="theme-storefront"
        [attr.data-theme-id]="theme.themeId"
        [attr.data-theme-version]="theme.themeVersion"
        [class.is-preview]="theme.preview"
        [attr.data-auth-experience]="theme.experience?.auth?.mode || 'modal'"
        [attr.data-auth-variant]="theme.experience?.auth?.variant || 'default'"
        [style.--theme-primary]="theme.tokens['primary']"
        [style.--theme-background]="theme.tokens['background']"
        [style.--theme-surface]="theme.tokens['surface']"
        [style.--theme-text]="theme.tokens['text']"
        [style.--theme-light-primary]="theme.tokens['light_primary'] || theme.tokens['primary']"
        [style.--theme-light-background]="theme.tokens['light_background'] || theme.tokens['background']"
        [style.--theme-light-surface]="theme.tokens['light_surface'] || theme.tokens['surface']"
        [style.--theme-light-text]="theme.tokens['light_text'] || theme.tokens['text']"
        [style.--theme-dark-primary]="theme.tokens['dark_primary'] || theme.tokens['primary']"
        [style.--theme-dark-background]="theme.tokens['dark_background'] || theme.tokens['background']"
        [style.--theme-dark-surface]="theme.tokens['dark_surface'] || theme.tokens['surface']"
        [style.--theme-dark-text]="theme.tokens['dark_text'] || theme.tokens['text']"
        [style.--theme-radius]="theme.tokens['radius']"
        [style.--theme-container]="theme.tokens['container']"
        [style.--theme-font-display]="theme.tokens['font_display']"
        [style.--theme-font-body]="theme.tokens['font_body']">
        <app-site-header />
        <div class="theme-rendered-page" [innerHTML]="renderedHtml()"></div>
        <app-site-footer />
      </div>
      <p class="theme-action-status" aria-live="polite">{{ actionStatus() }}</p>
    }
  `,
  styleUrl: './theme-storefront.component.scss'
})
export class ThemeStorefrontComponent implements OnDestroy {
  private readonly pageData = inject(PageDataService);
  private readonly api = inject(ApiClientService);
  private readonly cart = inject(CommerceCartService);
  private readonly i18n = inject(I18nService);
  private readonly analytics = inject(AnalyticsService);
  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly render = computed(() => this.pageData.get<ThemeRenderPayload>('themeRender'));
  protected readonly renderedHtml = computed<SafeHtml>(() => this.sanitizer.bypassSecurityTrustHtml(this.render()?.html ?? ''));
  protected readonly actionStatus = signal('');
  private actionPending = false;
  private carouselTimer:ReturnType<typeof setTimeout>|null=null;
  private carouselResizeTimer:ReturnType<typeof setTimeout>|null=null;
  private carouselConfigurations:ThemeCarouselExperience[]=[];
  private carouselInstances:Swiper[]=[];
  private motionTimer:ReturnType<typeof setTimeout>|null=null;
  private motionContext?:{revert():void};
  private interactionAbort?:AbortController;

  constructor() {
    effect(() => {
      if (typeof window === 'undefined') return;
      const theme = this.render();
      const existing = this.document.head?.querySelector<HTMLStyleElement>('style[data-theme-package]');
      if (!theme) { existing?.remove(); this.clearDocumentSurface(); return; }
      const style = existing ?? this.document.createElement('style');
      style.setAttribute('data-theme-package', theme.themeId);
      if (style.textContent !== theme.stylesheet) style.textContent = theme.stylesheet;
      if (!style.isConnected) this.document.head?.append(style);
      this.document.body?.classList.add('theme-storefront-active');
      this.document.body?.style.setProperty('--active-storefront-background',theme.tokens['background']||'var(--paper)');
    });
    effect(()=>{const carousels=this.render()?.experience?.carousels??[];if(!this.isBrowser)return;this.carouselConfigurations=carousels;if(this.carouselTimer)clearTimeout(this.carouselTimer);this.carouselTimer=setTimeout(()=>this.mountThemeCarousels(carousels),0)});
    effect(()=>{const theme=this.render();const themeId=theme?.themeId??'';const preview=theme?.preview===true;if(!this.isBrowser)return;if(this.motionTimer)clearTimeout(this.motionTimer);this.motionTimer=setTimeout(()=>{this.mountThemeInteractions(themeId);void this.mountThemeMotion(themeId,preview)},40)});
  }

  @HostListener('window:resize')
  protected handleCarouselViewport():void{if(!this.isBrowser)return;if(this.carouselResizeTimer)clearTimeout(this.carouselResizeTimer);this.carouselResizeTimer=setTimeout(()=>this.mountThemeCarousels(this.carouselConfigurations),180)}

  ngOnDestroy(): void { if(this.carouselTimer)clearTimeout(this.carouselTimer);if(this.carouselResizeTimer)clearTimeout(this.carouselResizeTimer);if(this.motionTimer)clearTimeout(this.motionTimer);this.motionContext?.revert();this.interactionAbort?.abort();this.destroyCarousels();this.document.head?.querySelector('style[data-theme-package]')?.remove();this.clearDocumentSurface(); }

  private clearDocumentSurface():void{this.document.body?.classList.remove('theme-storefront-active');this.document.body?.style.removeProperty('--active-storefront-background')}

  private mountThemeInteractions(themeId:string):void{
    this.interactionAbort?.abort();this.interactionAbort=undefined;
    if(themeId!=='falderyasi-aether'||!window.matchMedia('(hover: hover) and (pointer: fine)').matches)return;
    const root=this.host.nativeElement.querySelector<HTMLElement>('.theme-storefront[data-theme-id="falderyasi-aether"]');if(!root)return;
    const controller=new AbortController();this.interactionAbort=controller;
    const cardFrom=(target:EventTarget|null)=>target instanceof Element?target.closest<HTMLElement>('.oracle-feed-card'):null;
    root.addEventListener('pointerover',(event)=>{
      const card=cardFrom(event.target);if(!card||(event.relatedTarget instanceof Node&&card.contains(event.relatedTarget)))return;
      const video=card.querySelector<HTMLVideoElement>('.aether-feed-video');if(!video)return;
      root.querySelectorAll<HTMLVideoElement>('.aether-feed-video').forEach((item)=>{if(item===video)return;item.pause();item.closest('.oracle-feed-card')?.classList.remove('is-video-playing')});
      video.muted=true;video.loop=true;video.playsInline=true;video.preload='metadata';
      void video.play().then(()=>card.classList.add('is-video-playing')).catch(()=>card.classList.remove('is-video-playing'));
    },{signal:controller.signal});
    root.addEventListener('pointerout',(event)=>{
      const card=cardFrom(event.target);if(!card||(event.relatedTarget instanceof Node&&card.contains(event.relatedTarget)))return;
      const video=card.querySelector<HTMLVideoElement>('.aether-feed-video');if(!video)return;
      video.pause();try{video.currentTime=0}catch{/* Media may not have metadata yet. */}card.classList.remove('is-video-playing');
    },{signal:controller.signal});
  }

  private async mountThemeMotion(themeId:string,preview=false):Promise<void>{
    this.motionContext?.revert();this.motionContext=undefined;
    if(!['falderyasi-aether','bdsmbeds-nocturne'].includes(themeId)||window.matchMedia('(prefers-reduced-motion: reduce)').matches)return;
    const [{gsap},{ScrollTrigger}]=await Promise.all([import('gsap'),import('gsap/ScrollTrigger')]);
    if(this.render()?.themeId!==themeId)return;
    gsap.registerPlugin(ScrollTrigger);
    const root=this.host.nativeElement.querySelector<HTMLElement>(`.theme-storefront[data-theme-id="${themeId}"]`);if(!root)return;
    if(themeId==='bdsmbeds-nocturne'){
      this.motionContext=gsap.context(()=>{
        const headerItems=root.querySelectorAll(':scope>app-site-header .brand,:scope>app-site-header .desktop-nav>a,:scope>app-site-header .nav-dropdown,:scope>app-site-header .header-actions>*');
        if(headerItems.length)gsap.from(headerItems,{autoAlpha:0,y:-10,duration:.7,stagger:.055,ease:'power2.out',clearProps:'all'});
        const heroCopy=root.querySelectorAll('.nocturne-hero-copy>*');
        const heroImage=root.querySelector<HTMLElement>('.nocturne-hero-image');
        if(heroCopy.length)gsap.from(heroCopy,{autoAlpha:0,y:38,filter:'blur(7px)',duration:1,stagger:.09,ease:'power3.out',clearProps:'filter'});
        if(heroImage){gsap.from(heroImage,{autoAlpha:0,scale:1.055,duration:1.35,ease:'power3.out'});if(!preview)gsap.to(heroImage,{yPercent:6,scale:1.025,scrollTrigger:{trigger:'.nocturne-hero',start:'top top',end:'bottom top',scrub:1}})}
        if(preview)return;
        root.querySelectorAll<HTMLElement>('.aurora-values,.aurora-section,.nocturne-statement,.nocturne-craft,.aurora-editorial,.nocturne-journal,.aurora-newsletter').forEach((section)=>{
          const headings=section.querySelectorAll<HTMLElement>('h2,.nocturne-eyebrow,.aurora-section-heading>p');
          if(headings.length)gsap.from(headings,{autoAlpha:0,y:34,duration:.85,stagger:.07,ease:'power3.out',scrollTrigger:{trigger:section,start:'top 84%',once:true}});
          const cards=section.querySelectorAll<HTMLElement>('.atl-product-card,.aurora-collection-card,.nocturne-journal-grid article,.aurora-values article,.nocturne-materials span');
          if(cards.length)gsap.from(cards,{autoAlpha:0,y:42,duration:.75,stagger:.055,ease:'power3.out',scrollTrigger:{trigger:section,start:'top 78%',once:true}});
        });
      },root);
      ScrollTrigger.refresh();return;
    }
    this.motionContext=gsap.context(()=>{
      const headerItems=root.querySelectorAll(':scope>app-site-header .brand,:scope>app-site-header .desktop-nav>a,:scope>app-site-header .nav-dropdown,:scope>app-site-header .header-actions>*');
      if(headerItems.length)gsap.from(headerItems,{autoAlpha:0,y:-14,duration:.62,stagger:.045,ease:'power2.out',clearProps:'all'});
      const heroCopy=root.querySelectorAll('.aether-hero>div:first-child>*');
      const heroVisual=root.querySelector('.aether-hero .oracle-hero-visual');
      gsap.from(heroCopy,{autoAlpha:0,y:34,filter:'blur(8px)',duration:.9,stagger:.08,ease:'power3.out',clearProps:'filter'});
      if(heroVisual){gsap.from(heroVisual,{autoAlpha:0,scale:.88,rotate:4,duration:1.15,ease:'power3.out'});if(!preview)gsap.to(heroVisual,{yPercent:7,scrollTrigger:{trigger:'.aether-hero',start:'top top',end:'bottom top',scrub:1}})}
      if(preview)return;
      root.querySelectorAll<HTMLElement>('.oracle-sellers,.oracle-picker,.oracle-feed,.oracle-blog-grid,.oracle-products').forEach((section)=>{
        const heading=section.querySelector('header');
        const cards=section.querySelectorAll(':scope>div>article,:scope>div>a');
        if(!heading&&!cards.length)return;
        const timeline=gsap.timeline({scrollTrigger:{trigger:section,start:'top 82%',once:true}});
        if(heading)timeline.fromTo(heading,{autoAlpha:0,y:42},{autoAlpha:1,y:0,duration:.85,ease:'power3.out',immediateRender:false},0);
        if(cards.length)timeline.fromTo(cards,{autoAlpha:0,y:48,scale:.985},{autoAlpha:1,y:0,scale:1,duration:.82,stagger:.065,ease:'power3.out',immediateRender:false},heading ? .08 : 0);
      });
    },root);
    const refresh=()=>{if(root.isConnected&&this.render()?.themeId===themeId)ScrollTrigger.refresh()};
    requestAnimationFrame(()=>requestAnimationFrame(refresh));
    void this.document.fonts?.ready.then(refresh);
  }

  private destroyCarousels():void{
    for(const instance of this.carouselInstances)instance.destroy(true,true);this.carouselInstances=[];
    for(const container of Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('[data-theme-carousel="mounted"]'))){
      const wrapper=container.querySelector<HTMLElement>(':scope > .swiper-wrapper');const slides=wrapper?Array.from(wrapper.children).filter((node):node is HTMLElement=>node instanceof HTMLElement):[];
      for(const slide of slides){slide.classList.remove('swiper-slide','swiper-slide-active','swiper-slide-next','swiper-slide-prev');slide.removeAttribute('style')}
      if(slides.length)container.replaceChildren(...slides);else container.querySelectorAll(':scope > .theme-swiper-prev,:scope > .theme-swiper-next,:scope > .theme-swiper-pagination').forEach(node=>node.remove());
      container.classList.remove('swiper','swiper-initialized','swiper-horizontal','swiper-backface-hidden');container.removeAttribute('style');delete container.dataset['themeCarousel'];
    }
  }
  private mountThemeCarousels(configurations:ThemeCarouselExperience[]):void{
    this.destroyCarousels();
    for(const configuration of configurations){
      if(configuration.maxWidth&&window.innerWidth>configuration.maxWidth)continue;
      const container=this.host.nativeElement.querySelector<HTMLElement>(configuration.selector);if(!container||container.dataset['themeCarousel']==='mounted')continue;
      const slides=Array.from(container.children).filter((node):node is HTMLElement=>node instanceof HTMLElement);if(slides.length<2)continue;
      const wrapper=this.document.createElement('div');wrapper.className='swiper-wrapper';for(const slide of slides){slide.classList.add('swiper-slide');wrapper.appendChild(slide)}
      const previous=this.document.createElement('button');previous.type='button';previous.className='theme-swiper-prev';previous.setAttribute('aria-label','Önceki içerikler');previous.textContent='←';
      const next=this.document.createElement('button');next.type='button';next.className='theme-swiper-next';next.setAttribute('aria-label','Sonraki içerikler');next.textContent='→';
      const pagination=this.document.createElement('div');pagination.className='theme-swiper-pagination';container.replaceChildren(wrapper,previous,next,pagination);container.classList.add('swiper');container.dataset['themeCarousel']='mounted';
      const breakpoints=Object.fromEntries(Object.entries(configuration.breakpoints??{}).map(([width,value])=>[Number(width),value]));const largest=Math.max(typeof configuration.slidesPerView==='number'?configuration.slidesPerView:1,...Object.values(configuration.breakpoints??{}).map(value=>value.slidesPerView));
      this.carouselInstances.push(new Swiper(container,{modules:[A11y,Autoplay,Navigation,Pagination],slidesPerView:configuration.slidesPerView??1,spaceBetween:configuration.spaceBetween??18,breakpoints,grabCursor:true,watchOverflow:true,loop:configuration.loop===true&&slides.length>Math.ceil(largest),autoplay:configuration.autoplayMs?{delay:configuration.autoplayMs,disableOnInteraction:false,pauseOnMouseEnter:true}:false,navigation:{prevEl:previous,nextEl:next},pagination:{el:pagination,clickable:true,dynamicBullets:true}}));
    }
  }

  @HostListener('submit', ['$event'])
  protected async submit(event: SubmitEvent): Promise<void> {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || form.dataset['aymudoIsland'] !== 'add-to-cart') return;
    event.preventDefault();
    if (this.actionPending) return;
    const listingID = Number(new FormData(form).get('listingID') ?? 0);
    if (listingID <= 0) return;
    this.actionPending = true;
    this.actionStatus.set(this.i18n.lang() === 'tr' ? 'Sepete ekleniyor…' : 'Adding to cart…');
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      await firstValueFrom(this.api.post('cartAdd', { listingID, quantity: 1, country: this.i18n.country(), options: {}, customization: {} }));
      this.cart.remember({ listingID, title: form.closest('article')?.querySelector('h3')?.textContent?.trim() || 'Product', imageUrl: form.closest('article')?.querySelector('img')?.getAttribute('src') || null, quantity: 1 });
      this.analytics.track('cart.add', { entityType: 'listing', entityID: listingID, metadata: { source: 'theme_engine' } });
      this.cart.animateToCart(form.closest('article'));
      this.actionStatus.set(this.i18n.lang() === 'tr' ? 'Ürün sepete eklendi.' : 'Product added to cart.');
    } catch (error) {
      this.analytics.track('cart.error', { entityType: 'listing', entityID: listingID, metadata: { source: 'theme_engine', message: error instanceof Error ? error.message : 'cart_add_failed' } });
      this.actionStatus.set(this.i18n.lang() === 'tr' ? 'Ürün sepete eklenemedi.' : 'Product could not be added.');
    } finally {
      this.actionPending = false;
      if (button) button.disabled = false;
    }
  }
}
